/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { PHASE } from './constants';
import { checkROMID } from './check_romid';
import { checkDTC } from './check_dtc';
import { checkMap } from './check_map';
import { clearMap } from './clear_map';
import { doDispose } from './dispose';

export default {
  name: 'requirement40001',
  data() {
    return {
      phase: PHASE.WAITING,
      isLoading: false,
      systemId: '',
      modal_requirement_40001: false,
      selectedMode: '',
      // 機能を選択していないときにOKボタンを非活性とするフラグ
      isNotSelected: true,
      mapName: '',
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.RECORDED_ERROR:
        case PHASE.RECORDED_CONFIRMATION_ERROR:
        case PHASE.SPEED_ERROR:
        case PHASE.ECU_ERROR:
        case PHASE.TIMEOUT_ERROR:
        case PHASE.COMMUNICATION_ERROR:
        case PHASE.EXCLUDED_ERROR:
          return 'danger';
        case PHASE.MAP_DISPLAY_SUCCESS:
        case PHASE.CLEARING_SUCCESS:
          return 'success';
        case PHASE.SELECTING:
        case PHASE.WAITING:
        case PHASE.CLEARING_CONFIRMATION:
        default:
          return 'secondary';
      }
    },

    headerTitle() {
      // 選択, 消去確認
      const ConfirmationTitle = 'message.common_title_confirmation';
      // 待機
      const ExecutionTitle = 'individual.common_title_execution';
      // マップ名称表示, 消去正常終了
      const SuccessTitle = 'message.common_title_success';
      // 記録済み, 記録済み確認, 車速エラー, EUC異常, タイムアウトエラー, 通信異常, 車両対象外
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        selecting: ConfirmationTitle,
        recorded_error: ErrorTitle,
        recorded_confirmation_error: ErrorTitle,
        map_display_success: SuccessTitle,
        waiting: ExecutionTitle,
        clearing_confirmation: ConfirmationTitle,
        clearing_success: SuccessTitle,
        speed_error: ErrorTitle,
        ecu_error: ErrorTitle,
        timeout_error: ErrorTitle,
        communication_error: ErrorTitle,
        excluded_error: ErrorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    workSupportMessage() {
      const messageMap = {
        selecting: null,
        recorded_error: `${this.i18nWsKey}.REQ_WS_40001_TXT_7`,
        recorded_confirmation_error: `${this.i18nWsKey}.REQ_WS_40001_TXT_8`,
        map_display_success: `${this.i18nWsKey}.REQ_WS_40001_TXT_9`,
        waiting: `${this.i18nWsKey}.REQ_WS_40001_TXT_10`,
        clearing_confirmation: `${this.i18nWsKey}.REQ_WS_40001_TXT_11`,
        clearing_success: `${this.i18nWsKey}.REQ_WS_40001_TXT_12`,
        speed_error: `${this.i18nWsKey}.REQ_WS_40001_TXT_13`,
        ecu_error: `${this.i18nWsKey}.REQ_WS_40001_TXT_14`,
        timeout_error: `${this.i18nWsKey}.REQ_WS_40001_TXT_15`,
        communication_error: `${this.i18nWsKey}.REQ_WS_40001_TXT_16`,
        excluded_error: `${this.i18nWsKey}.REQ_WS_40001_TXT_17`,
      };
      if (this.phase === PHASE.MAP_DISPLAY_SUCCESS) {
        return this.$t(messageMap[this.phase], [this.mapName]);
      } else {
        return this.$t(messageMap[this.phase]);
      }
    },

    showOKButton() {
      const showOKButtonMap = {
        selecting: false,
        clearing_confirmation: false,
        waiting: false,
        map_display_success: true,
        clearing_success: true,
        recorded_error: true,
        recorded_confirmation_error: true,
        speed_error: true,
        ecu_error: true,
        timeout_error: true,
        communication_error: true,
        excluded_error: true,
      };
      return showOKButtonMap[this.phase];
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_40001 = true;
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_40001 = false;
    },

    /** 機能選択一覧表示 */
    setModeSelectList() {
      this.modeList = [
        { value: "confirm", text: this.$t(`${this.i18nWsKey}.REQ_WS_40001_TXT_1`) },
        { value: "clear", text: this.$t(`${this.i18nWsKey}.REQ_WS_40001_TXT_2`) }
      ];
    },

    /** 機能が選択され、OKボタン押下時の処理 */
    async selectMode() {
      if (this.selectedMode === 'confirm') {
        // DTC確認処理実行
        await this.checkDTC();
      } else if (this.selectedMode === 'clear') {
        // 確認画面表示
        this.phase = PHASE.CLEARING_CONFIRMATION;
      }
    },

    /** 機能が選択されたときにOKボタンを押下できるようにする */
    handleSelected() {
      this.isNotSelected = false;
    },

    /** 選択画面へ遷移 */
    toSelecting() {
      this.phase = PHASE.SELECTING;
    },

    /** 終了処理 */
    async exit() {
      await this.doDispose();
      this.closeWizard();
    },

    /** ROMID確認 */
    async checkROMID() {
      await checkROMID(this);
    },

    /** DTC確認 */
    async checkDTC() {
      await checkDTC(this);
    },

    /** マップNo確認 */
    async checkMap() {
      await checkMap(this);
    },

    /** マップ消去 */
    async clearMap() {
      await clearMap(this);
    },

    /** Dispose APIの実行 */
    async doDispose() {
      await doDispose(this);
    },


    /** 通信異常画面の表示 */
    showUnexpectedError() {
      // 通信異常画面に遷移
      this.phase = PHASE.COMMUNICATION_ERROR;
    },

    /**
      * API 通信で共通バリデーションでエラーが発生した際のコールバック
      * @param {object} result バリデーション結果
      */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** OKボタン押下時の処理 */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.SELECTING:
          await this.selectMode();
          break;
        case PHASE.RECORDED_ERROR:
        case PHASE.MAP_DISPLAY_SUCCESS:
        case PHASE.CLEARING_SUCCESS:
        case PHASE.SPEED_ERROR:
        case PHASE.ECU_ERROR:
          this.toSelecting();
          break;
        case PHASE.RECORDED_CONFIRMATION_ERROR:
          await this.checkMap();
          break;
        case PHASE.TIMEOUT_ERROR:
        case PHASE.COMMUNICATION_ERROR:
        case PHASE.EXCLUDED_ERROR:
          await this.exit();
          break;
        default:
          break;
      }
    },

    /** 戻るボタン押下時の処理 */
    async onBackClicked() {
      await this.exit();
    },

    /** NOボタン押下時の処理 */
    async onNoClicked() {
      this.toSelecting();
    },

    /** YESボタン押下時の処理 */
    async onYesClicked() {
      await this.clearMap();
    },
  },

  async mounted() {
    // 機能一覧作成
    this.setModeSelectList();
    this.init();
    // データ読み出し
    await this.checkROMID();
  },
};